import Service from '@ember/service';

import { action } from '@ember/object';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import { tracked } from 'tracked-built-ins';

import { complex, labelOrConfig, refreshes } from 'rise-ui/utils/query-params.js';
import { readOnly } from 'rise-ui/utils/object/computed.js';
import { linkTo } from 'rise-ui/utils/link-to.js';
import { ParamsHandler } from 'rise-ui/utils/params-handler.js';

// Map from service API param identifiers to their URL query-param keys
export const qp = {
  filter: 'filter',
  search: 'search',
  order:  'order',
  page:   'page',
};

// FIXME: This is not very composable
const crefresh = p => labelOrConfig(p, refreshes(complex()));

export const qpRoute = {
  filter: crefresh(qp.filter),
  search: refreshes(qp.search),
  order: refreshes(qp.order),
  page: refreshes(qp.page),
};

export const queryParamKeys = Object.keys(qp);
export const queryParams = Object.values(qp);

export const timeSeriesSearchRouteQueryParams
  = queryParamKeys.reduce((params, key) => {
    return { ...params, ...qpRoute[key] };
  }, {});

export default class TimeSeriesSearchService extends Service {
  @service ssr;
  @service routing;
  @service('time-series/search-filters') searchFilters;
  @service('time-series/search-results') searchResults;

  routeName = 'time-series.search';

  paramsHandler = new ParamsHandler();
  @readOnly('paramsHandler.params') params;
  @action setParams() {
    this.paramsHandler.setParams(...arguments);
    return this;
  }

  @readOnly('searchFilters.filters') filters;
  @readOnly('searchResults.results') results;
  @readOnly('searchResults.meta') meta;

  @tracked _editedTerm;

  get editedTerm() {
    return this._editedTerm ?? this.params[qp.search];
  }

  @action editTerm(term) {
    this._editedTerm = term;
    return this;
  }

  get isTermDirty() {
    return (this.editedTerm || '') !== (this.params[qp.search] || '');
  }

  @cached get selectedFilters() {
    const selections = {};
    const filters = this.params[qp.filter];
    if (filters) {
      Object.keys(filters).forEach(key => {
        selections[key] = filters[key] ? [ ...new Set([].concat(filters[key])) ] : [];
      });
    }
    return selections;
  }

  @tracked _editedFilters = {};

  @cached get editedFilters() {
    const { _editedFilters, selectedFilters } = this;
    const searchFilters = {};
    this.filters?.value?.forEach?.(({ key }) => {
      const selections = _editedFilters[key] ?? selectedFilters?.[key];
      if (selections) {
        searchFilters[key] = [ ...new Set(selections) ];
      }
    });
    return searchFilters;
  }

  @action editFilters(searchFilters) {
    this._editedFilters = searchFilters;
    return this;
  }

  @action editFilterByKey(key, selections) {
    const searchFilters = { ...this.editedFilters };
    searchFilters[key] = [ ...selections ];
    this.editFilters(searchFilters);
  }

  @cached get isFiltersDirty() {
    const { editedFilters: e, filters: f } = this;
    return this.filters?.value?.some?.(({ key }) => e[key] && (!f?.[key] ? true :
      [ ...new Set(e[key]) ].sort().join('|') !== [ ...new Set(f[key]) ].sort().join('|')
    ));
  }

  @cached get isDirty() {
    return this.isTermDirty || this.isFiltersDirty;
  }
  @readOnly('results.isPending') isPending;

  clearEdit() {
    this._editedTerm = undefined;
    this._editedFilters = {};
    return this;
  }

  @cached get linkToCommit() {
    const { editedFilters: filters, editedTerm } = this;
    return linkTo(this.routeName, {
      [qp.search]: editedTerm || undefined,
      [qp.filter]: filters && !Object.values(filters).every(selections => !selections?.length)
        ? filters : undefined,
      [qp.page]: undefined,
    });
  }

  @cached get linkToClear() {
    return linkTo(this.routeName, {
      [qp.search]: undefined,
      [qp.filter]: undefined,
      [qp.page]: undefined,
    });
  }

  @cached get paging() {
    return {
      page: this.params[qp.page] || 1,
      items: this.meta.totalItems,
      itemsPerPage: this.meta.itemsPerPage,
      pending: this.results?.isPending,
      disabled: this.results?.isPending,
    };
  }
}
